// app/javascript/controllers/table_builder_controller.js
import { Controller } from "stimulus"

const MAX_PASTE_ROWS = 500

export default class extends Controller {
  static targets = ["table", "dataInput"]

  connect() {
    // Cells render as plain text and edit on click via one delegated listener —
    // per-cell inputs made large tables (hundreds of rows) unusably slow.
    this.handleTableClick = this.handleTableClick.bind(this)
    this.handleEditorBlur = this.handleEditorBlur.bind(this)
    this.handleEditorKeydown = this.handleEditorKeydown.bind(this)
    this.handleEditorPaste = this.handleEditorPaste.bind(this)
    if (this.hasTableTarget) {
      this.tableTarget.addEventListener('click', this.handleTableClick)
    }

    // Debug: Check if targets exist
    console.log("Controller element:", this.element)
    console.log("Has table target:", this.hasTableTarget)
    console.log("Has dataInput target:", this.hasDataInputTarget)
    
    // Get initial data from data attribute (Rails 6 way)
    const initialDataAttr = this.element.dataset.tableBuilderInitialDataValue
    console.log("Initial data attribute:", initialDataAttr)
    
    let initialData = {}
    if (initialDataAttr) {
      try {
        initialData = JSON.parse(initialDataAttr)
      } catch (e) {
        console.error("Error parsing initial data:", e)
      }
    }
    
    // Initialize with existing data or create empty table
    if (initialData && 
        typeof initialData === 'object' && 
        initialData.headers && 
        initialData.rows) {
      this.tableData = initialData
    } else {
      this.tableData = {
        headers: ["Column 1", "Column 2"],
        rows: [
          ["Sample data 1", "Sample data 2"],
          ["Sample data 3", "Sample data 4"]
        ]
      }
    }
    
    console.log("Final data:", this.tableData)
    
    // Use setTimeout to ensure DOM is fully rendered by Cocoon
    setTimeout(() => {
      console.log("After timeout - Has table target:", this.hasTableTarget)
      if (this.hasTableTarget) {
        this.renderTable()
        this.updateHiddenInput()
      } else {
        console.error("Table target still not found!")
        // Let's see what's actually in the element
        console.log("Element children:", this.element.children)
        const targets = this.element.querySelectorAll('[data-table-builder-target]')
        console.log("Found target elements:", targets)
      }
    }, 300)
  }

  templateSelected(event) {
    console.log("Template selected:", event.target.value)
    const templateKey = event.target.value
    if (!templateKey) return
    
    const jobId = this.element.dataset.jobId || this.getJobIdFromUrl()
    
    console.log("Fetching template:", templateKey)
    
    fetch(`/job_report_builders/table_template?template_key=${templateKey}&job_id=${jobId}`)
      .then(response => {
        console.log("Response status:", response.status)
        return response.json()
      })
      .then(data => {
        console.log("Received data:", data)
        
        if (data.multiple_tables && data.tables) {
          // Handle multiple tables
          this.createMultipleTables(data.tables)
        } else {
          // Handle single table (existing logic)
          this.tableData = data
          this.renderTable()
          this.updateHiddenInput()
        }
      })
      .catch(error => {
        console.error("Fetch error:", error)
      })
  }

  // Add this new method
  createMultipleTables(tables) {
    if (!tables || tables.length === 0) return
    
    // First table replaces current one
    this.tableData = tables[0]
    this.renderTable()
    this.updateHiddenInput()
    
    if (tables[0].name) {
      this.setTableName(tables[0].name)
    }
    
    // Add additional tables with longer delays
    tables.slice(1).forEach((tableData, index) => {
      setTimeout(() => {
        console.log(`Creating table ${index + 2}: ${tableData.name}`)
        this.addNewTableWithData(tableData)
      }, (index + 1) * 500) // Increased from 200ms to 500ms
    })
  }

  // Add this new method
  addNewTableWithData(tableData) {
    // Find the specific Add Table button for the current section
    // Start from the current table's controller element and go up to find the section
    const currentSection = this.element.closest('.nested-fields.mb-4') // This finds the section container
    
    if (currentSection) {
      // Look for the "Add Table" link within this specific section
      const addTableLink = currentSection.querySelector('a.add_fields[data-association="job_report_table"]')
      
      console.log("Current section:", currentSection)
      console.log("Found section's Add Table link:", addTableLink)
      
      if (addTableLink) {
        addTableLink.click()
        
        setTimeout(() => {
          // Find the newest table within THIS section only
          const sectionTables = currentSection.querySelectorAll('[data-controller="table-builder"]')
          const newestTable = sectionTables[sectionTables.length - 1]
          
          if (newestTable && newestTable !== this.element) {
            const newController = this.application.getControllerForElementAndIdentifier(newestTable, 'table-builder')
            
            if (newController) {
              newController.tableData = {
                headers: tableData.headers,
                rows: tableData.rows
              }
              newController.renderTable()
              newController.updateHiddenInput()
              
              if (tableData.name) {
                newController.setTableName(tableData.name)
              }
              
              console.log("Successfully populated new table in correct section:", tableData.name)
            }
          }
        }, 250)
      } else {
        console.error("Could not find Add Table link in current section")
      }
    } else {
      console.error("Could not find current section")
      // Fallback to the original approach
      this.addNewTableWithDataFallback(tableData)
    }
  }

  addNewTableWithDataFallback(tableData) {
    const allButtons = document.querySelectorAll('a.add_fields')
    let addTableButton = null
    
    allButtons.forEach(button => {
      if (button.textContent.trim().includes('Add Table')) {
        addTableButton = button
      }
    })
    
    if (addTableButton) {
      addTableButton.click()
      // ... rest of the original logic
    }
  }

  populateNewTable(tableData, attempt = 1) {
    console.log(`Attempting to populate new table (attempt ${attempt})`)
    
    // Find the newest table on the page
    const allTables = document.querySelectorAll('[data-controller="table-builder"]')
    const newestTable = allTables[allTables.length - 1]
    
    console.log("Total tables found:", allTables.length)
    console.log("Current table element:", this.element)
    console.log("Newest table element:", newestTable)
    
    if (newestTable && newestTable !== this.element) {
      const newController = this.application.getControllerForElementAndIdentifier(newestTable, 'table-builder')
      
      console.log("New controller found:", !!newController)
      
      if (newController) {
        console.log("Setting table data:", tableData.name)
        
        newController.tableData = {
          headers: tableData.headers,
          rows: tableData.rows
        }
        newController.renderTable()
        newController.updateHiddenInput()
        
        if (tableData.name) {
          newController.setTableName(tableData.name)
        }
        
        console.log("Successfully populated new table:", tableData.name)
      } else {
        // If controller not found, try again with a longer delay
        if (attempt < 3) {
          console.log("Controller not ready, retrying...")
          setTimeout(() => {
            this.populateNewTable(tableData, attempt + 1)
          }, 200)
        } else {
          console.error("Could not get controller for new table after 3 attempts")
        }
      }
    } else {
      if (attempt < 3) {
        console.log("New table not ready, retrying...")
        setTimeout(() => {
          this.populateNewTable(tableData, attempt + 1)
        }, 200)
      } else {
        console.error("Could not find new table element after 3 attempts")
      }
    }
  }

  // Add this helper method
  setTableName(name) {
    // Find the name input field for this specific table
    // Look within the immediate parent container of this table controller
    const tableContainer = this.element.closest('.nested-fields.mb-6')
    
    if (tableContainer) {
      const nameInput = tableContainer.querySelector('input[placeholder="Table name"]')
      if (nameInput) {
        nameInput.value = name
        console.log("Set table name:", name)
      } else {
        console.error("Could not find name input in table container")
      }
    } else {
      // Fallback to the original approach
      const nameInput = this.element.querySelector('input[id*="name"]')
      if (nameInput) {
        nameInput.value = name
        console.log("Set table name (fallback):", name)
      } else {
        console.error("Could not find name input field")
      }
    }
  }

  getJobIdFromUrl() {
    // Extract job ID from current URL if needed
    const matches = window.location.pathname.match(/\/jobs\/(\d+)/)
    return matches ? matches[1] : null
  }

  addColumn() {
    // Safety check
    if (!this.tableData || !this.tableData.headers) {
      console.error("Data not properly initialized")
      return
    }

    this.commitActiveEdit()

    const columnNumber = this.tableData.headers.length + 1
    this.tableData.headers.push(`Column ${columnNumber}`)

    // Add empty cell to each existing row
    this.tableData.rows.forEach(row => {
      row.push("")
    })

    this.renderTable()
    this.updateHiddenInput()
    this.scrollToRight() // keep the new column and the + button in view
  }

  addRow() {
    // Safety check
    if (!this.tableData || !this.tableData.headers) {
      console.error("Data not properly initialized")
      return
    }

    this.commitActiveEdit()

    // Create new row with empty cells for each column
    const newRow = new Array(this.tableData.headers.length).fill("")
    this.tableData.rows.push(newRow)
    
    this.renderTable()
    this.updateHiddenInput()
  }

  removeColumn(event) {
    const columnIndex = parseInt(event.target.dataset.columnIndex)

    // Don't allow removing the last column
    if (this.tableData.headers.length <= 1) {
      alert("Table must have at least one column")
      return
    }

    this.commitActiveEdit()

    this.tableData.headers.splice(columnIndex, 1)
    this.tableData.rows.forEach(row => {
      row.splice(columnIndex, 1)
    })
    
    this.renderTable()
    this.updateHiddenInput()
  }

  removeRow(event) {
    const rowIndex = parseInt(event.target.dataset.rowIndex)

    // Don't allow removing the last row
    if (this.tableData.rows.length <= 1) {
      alert("Table must have at least one row")
      return
    }

    this.commitActiveEdit()

    this.tableData.rows.splice(rowIndex, 1)
    
    this.renderTable()
    this.updateHiddenInput()
  }

  updateHeader(event) {
    const columnIndex = parseInt(event.target.dataset.columnIndex)
    this.tableData.headers[columnIndex] = event.target.value
    this.updateHiddenInput()
  }

  disconnect() {
    if (this.hasTableTarget) {
      this.tableTarget.removeEventListener('click', this.handleTableClick)
    }
  }

  // --- Click-to-edit cells ---------------------------------------------------
  // Body cells are plain <td>s; a single delegated click listener swaps in a
  // temporary <input>. Commit on blur/Enter, cancel on Escape, Tab/Enter move
  // between cells. The hidden JSON field only updates on commit.

  handleTableClick(event) {
    const removeButton = event.target.closest('.remove-row-btn')
    if (removeButton) {
      this.removeRow({ target: removeButton })
      return
    }

    const cell = event.target.closest('td.editable-cell')
    if (cell && !cell.contains(this.activeEditor)) {
      this.startCellEdit(cell)
    }
  }

  startCellEdit(cell) {
    this.commitActiveEdit()

    const rowIndex = parseInt(cell.dataset.rowIndex)
    const columnIndex = parseInt(cell.dataset.columnIndex)

    const input = document.createElement('input')
    input.type = 'text'
    input.value = (this.tableData.rows[rowIndex] || [])[columnIndex] || ''
    input.dataset.rowIndex = rowIndex
    input.dataset.columnIndex = columnIndex
    input.className = 'w-full border-none bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent rounded px-1'
    input.addEventListener('blur', this.handleEditorBlur)
    input.addEventListener('keydown', this.handleEditorKeydown)
    input.addEventListener('paste', this.handleEditorPaste)

    cell.textContent = ''
    cell.appendChild(input)
    this.activeEditor = input
    input.focus()
    input.select()
  }

  commitActiveEdit() {
    const input = this.activeEditor
    if (!input) return

    this.activeEditor = null
    if (!input.isConnected) return // a re-render (e.g. multi-cell paste) already consumed it

    const rowIndex = parseInt(input.dataset.rowIndex)
    const columnIndex = parseInt(input.dataset.columnIndex)
    const value = input.value
    const cell = input.parentElement

    this.tableData.rows[rowIndex][columnIndex] = value
    cell.textContent = value
    this.updateHiddenInput()
  }

  cancelActiveEdit() {
    const input = this.activeEditor
    if (!input) return

    this.activeEditor = null
    if (!input.isConnected) return

    const rowIndex = parseInt(input.dataset.rowIndex)
    const columnIndex = parseInt(input.dataset.columnIndex)
    input.parentElement.textContent = (this.tableData.rows[rowIndex] || [])[columnIndex] || ''
  }

  handleEditorBlur(event) {
    if (event.target === this.activeEditor) this.commitActiveEdit()
  }

  handleEditorKeydown(event) {
    const input = event.target
    const rowIndex = parseInt(input.dataset.rowIndex)
    const columnIndex = parseInt(input.dataset.columnIndex)

    if (event.key === 'Enter') {
      event.preventDefault()
      this.commitActiveEdit()
      this.editCellAt(rowIndex + 1, columnIndex)
    } else if (event.key === 'Tab') {
      event.preventDefault()
      this.commitActiveEdit()
      const nextColumn = columnIndex + (event.shiftKey ? -1 : 1)
      this.editCellAt(rowIndex, nextColumn)
    } else if (event.key === 'Escape') {
      this.cancelActiveEdit()
    }
  }

  handleEditorPaste(event) {
    if (this.isMultiCell(this.clipboardText(event))) {
      // Discard the in-progress edit — the pasted grid supersedes it and the
      // re-render below replaces the editor anyway.
      this.activeEditor = null
    }
    this.pasteCells(event)
  }

  editCellAt(rowIndex, columnIndex) {
    const cell = this.tableTarget.querySelector(
      `td.editable-cell[data-row-index="${rowIndex}"][data-column-index="${columnIndex}"]`
    )
    if (cell) this.startCellEdit(cell)
  }

  // --- Paste-from-Excel support ---------------------------------------------
  // Excel/Sheets put copied ranges on the clipboard as tab-separated text.
  // Pasting a range into a cell fills down-right from that cell (Excel anchor
  // semantics), auto-adding rows and columns as needed. Single values fall
  // through to the browser's normal paste.

  pasteCells(event) {
    const text = this.clipboardText(event)
    if (!this.isMultiCell(text)) return

    event.preventDefault()
    const grid = this.parseClipboard(text)
    this.writeGrid(
      grid,
      parseInt(event.target.dataset.rowIndex),
      parseInt(event.target.dataset.columnIndex)
    )
    this.renderTable()
    this.updateHiddenInput()
  }

  // Pasting a range into a column header treats the first clipboard row as
  // header names and any remaining rows as data starting at row 0 — so a
  // whole table (headers included) can be pasted into the first header cell.
  pasteHeaders(event) {
    const text = this.clipboardText(event)
    if (!this.isMultiCell(text)) return

    event.preventDefault()
    this.commitActiveEdit()
    const grid = this.parseClipboard(text)
    const startCol = parseInt(event.target.dataset.columnIndex)

    const headers = grid.shift() || []
    headers.forEach((name, i) => {
      this.ensureColumn(startCol + i)
      if (name.trim() !== "") this.tableData.headers[startCol + i] = name.trim()
    })

    if (grid.length > 0) this.writeGrid(grid, 0, startCol)
    this.renderTable()
    this.updateHiddenInput()
  }

  clipboardText(event) {
    return event.clipboardData ? event.clipboardData.getData("text/plain") : ""
  }

  isMultiCell(text) {
    const trimmed = text.trim()
    return trimmed.includes("\t") || /[\r\n]/.test(trimmed)
  }

  parseClipboard(text) {
    const lines = text.replace(/\r\n?/g, "\n").split("\n")
    while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop()

    if (lines.length > MAX_PASTE_ROWS) {
      alert(`Paste is limited to ${MAX_PASTE_ROWS} rows at a time; the first ${MAX_PASTE_ROWS} were used.`)
      lines.length = MAX_PASTE_ROWS
    }

    return lines.map(line => line.split("\t"))
  }

  ensureColumn(index) {
    while (this.tableData.headers.length <= index) {
      this.tableData.headers.push(`Column ${this.tableData.headers.length + 1}`)
      this.tableData.rows.forEach(row => row.push(""))
    }
  }

  ensureRow(index) {
    while (this.tableData.rows.length <= index) {
      this.tableData.rows.push(new Array(this.tableData.headers.length).fill(""))
    }
  }

  writeGrid(grid, startRow, startCol) {
    grid.forEach((cells, r) => {
      this.ensureRow(startRow + r)
      const row = this.tableData.rows[startRow + r]
      cells.forEach((value, c) => {
        this.ensureColumn(startCol + c)
        // Existing rows can be ragged; pad so direct assignment never leaves holes
        while (row.length <= startCol + c) row.push("")
        row[startCol + c] = value
      })
    })
  }

  renderTable() {
    // Check if table target exists before trying to render
    if (!this.hasTableTarget) {
      console.error("Cannot render table - target not found")
      return
    }

    // innerHTML replacement destroys the scroll container; remember its
    // position so structural edits don't snap a wide table back to the left.
    const previousScrollLeft = this.scrollContainer() ? this.scrollContainer().scrollLeft : 0

    const tableHtml = `
      <div class="overflow-auto" style="max-height: 70vh;">
        <table class="min-w-full divide-y divide-gray-200 border border-gray-300">
          <thead class="bg-gray-50">
            <tr class="sortable-header-row">
              ${this.tableData.headers.map((header, colIndex) => `
                <th class="px-3 py-2 border border-gray-300 sticky top-0 z-10 bg-gray-100 sortable-header" data-column-index="${colIndex}">
                  <div class="flex items-center gap-1">
                    <div class="drag-handle cursor-move p-1 text-gray-400 hover:text-gray-600" title="Drag to reorder">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value="${this.escapeHtml(header)}"
                      data-column-index="${colIndex}"
                      data-action="input->table-builder#updateHeader paste->table-builder#pasteHeaders"
                      class="flex-1 border-none bg-transparent font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent rounded px-1"
                    />
                  </div>
                  <button
                    type="button"
                    data-column-index="${colIndex}"
                    data-action="click->table-builder#removeColumn"
                    class="absolute top-0 right-0 -mt-1 -mr-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs hover:bg-red-600 focus:outline-none"
                    title="Remove column"
                  >×</button>
                </th>
              `).join('')}
              <th class="px-3 py-2 w-12 sticky top-0 z-10 bg-gray-100">
                <button
                  type="button"
                  data-action="click->table-builder#addColumn"
                  class="bg-blue-500 text-white rounded px-2 py-1 text-xs hover:bg-blue-600 focus:outline-none"
                  title="Add column"
                >+</button>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            ${this.tableData.rows.map((row, rowIndex) => `
              <tr>
                ${row.map((cell, colIndex) => `
                  <td class="editable-cell px-3 py-2 border border-gray-300 cursor-text hover:bg-gray-100"
                      data-row-index="${rowIndex}"
                      data-column-index="${colIndex}"
                  >${this.escapeHtml(cell)}</td>
                `).join('')}
                <td class="px-3 py-2 w-12">
                  <button
                    type="button"
                    data-row-index="${rowIndex}"
                    class="remove-row-btn bg-red-500 text-white rounded px-2 py-1 text-xs hover:bg-red-600 focus:outline-none"
                    title="Remove row"
                  >×</button>
                </td>
              </tr>
            `).join('')}
            <tr>
              <td colspan="${this.tableData.headers.length}" class="px-3 py-2 text-center">
                <button 
                  type="button"
                  data-action="click->table-builder#addRow"
                  class="bg-blue-500 text-white rounded px-3 py-1 text-sm hover:bg-blue-600 focus:outline-none"
                >Add Row</button>
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    `

    this.tableTarget.innerHTML = tableHtml
    this.activeEditor = null // any open editor was just destroyed with the old DOM
    if (this.scrollContainer()) this.scrollContainer().scrollLeft = previousScrollLeft

    // Initialize sortable columns
    this.initializeSortableColumns()
  }

  scrollContainer() {
    return this.hasTableTarget ? this.tableTarget.querySelector('.overflow-auto') : null
  }

  scrollToRight() {
    const container = this.scrollContainer()
    if (container) container.scrollLeft = container.scrollWidth
  }

  initializeSortableColumns() {
    const headerRow = this.tableTarget.querySelector('.sortable-header-row')

    if (!headerRow) {
      console.error("Could not find sortable header row")
      return
    }

    $(headerRow).sortable({
      items: '.sortable-header',
      handle: '.drag-handle',
      axis: 'x',
      cursor: 'move',
      tolerance: 'pointer',
      placeholder: 'sortable-placeholder',
      helper: 'clone',
      start: (event, ui) => {
        // Add placeholder styling
        ui.placeholder.height(ui.helper.height())
        ui.placeholder.css('background-color', '#e5e7eb')
      },
      stop: (event, ui) => {
        // Reorder the data based on new column positions
        this.reorderColumns()
      }
    })
  }

  reorderColumns() {
    this.commitActiveEdit()

    // Get the new order of column indices from the DOM
    const headerCells = this.tableTarget.querySelectorAll('.sortable-header')
    const newOrder = Array.from(headerCells).map(cell => parseInt(cell.dataset.columnIndex))

    console.log("Reordering columns to:", newOrder)

    // Create new headers array based on new order
    const newHeaders = newOrder.map(oldIndex => this.tableData.headers[oldIndex])

    // Create new rows with reordered columns
    const newRows = this.tableData.rows.map(row => {
      return newOrder.map(oldIndex => row[oldIndex])
    })

    // Update the table data
    this.tableData.headers = newHeaders
    this.tableData.rows = newRows

    // Re-render the table to update column indices
    this.renderTable()

    // Update the hidden input
    this.updateHiddenInput()

    console.log("Columns reordered successfully")
  }

  updateHiddenInput() {
    if (this.hasDataInputTarget) {
      this.dataInputTarget.value = JSON.stringify(this.tableData)
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}